<script setup lang="ts">
import { onMounted, watch, computed } from 'vue';
import { useHead } from '@unhead/vue';
import { useClipsStore } from '../stores/clipsStore';
import ClipGrid from '../components/ClipGrid.vue';
import AdBanner from '../components/ads/AdBanner.vue';

const clipsStore = useClipsStore();

// Dynamic SEO meta tags based on current filter
const pageTitle = computed(() => {
  if (clipsStore.selectedActorId) {
    const actor = clipsStore.actors.find(a => a.id === clipsStore.selectedActorId);
    if (actor) {
      return `${actor.name} Comedy Dialogues & Meme Audio | Tamil Soundboard D-PRO`;
    }
  }
  if (clipsStore.selectedTag) {
    return `${clipsStore.selectedTag} Tamil Meme Audio Clips | Tamil Soundboard D-PRO`;
  }
  if (clipsStore.searchQuery) {
    return `"${clipsStore.searchQuery}" - Tamil Comedy Audio Search | Tamil Soundboard D-PRO`;
  }
  return 'Tamil Soundboard D-PRO | 1000+ Tamil Meme Audio & Comedy Clips';
});

const pageDescription = computed(() => {
  if (clipsStore.selectedActorId) {
    const actor = clipsStore.actors.find(a => a.id === clipsStore.selectedActorId);
    if (actor) {
      return `🎵 Listen to ${actor.name}'s best comedy dialogues and punch lines. Download free Tamil meme audio clips for WhatsApp, Instagram reels & memes. ${actor.name} காமெடி டயலாக்ஸ்.`;
    }
  }
  return '🎵 Free Tamil comedy soundboard with 1000+ iconic dialogues from Vadivelu, Goundamani, Santhanam & more! Perfect for WhatsApp status, Instagram reels, memes.';
});

// Set dynamic head tags
useHead({
  title: pageTitle,
  meta: [
    { name: 'description', content: pageDescription },
    { property: 'og:title', content: pageTitle },
    { property: 'og:description', content: pageDescription },
  ],
});

onMounted(() => {
  clipsStore.fetchClips();
  clipsStore.fetchActors();
  clipsStore.fetchTags();
});

// Watch for filter changes
watch(
  () => [clipsStore.searchQuery, clipsStore.selectedActorId, clipsStore.selectedTag],
  () => {
    // Clips are fetched in the respective setter actions
  }
);

function handleLoadMore() {
  clipsStore.loadMore();
}
</script>

<template>
  <div class="max-w-7xl mx-auto">
    <!-- SEO Content Header (visible but styled subtly) -->
    <header class="mb-6 text-center md:text-left">
      <h1 class="sr-only">Tamil Soundboard D-PRO - Free Tamil Meme Audio & Comedy Clips</h1>
      <p class="text-slate-500 text-xs">
        {{ clipsStore.totalClips }}+ Tamil comedy dialogues from your favorite actors
      </p>
    </header>

    <!-- Top Ad Banner -->
    <div class="mb-6">
      <AdBanner slot="1234567890" format="horizontal" />
    </div>

    <!-- Main Content Grid -->
    <ClipGrid :clips="clipsStore.clips" :loading="clipsStore.loading" />

    <!-- Middle Ad Banner (after first batch) -->
    <div v-if="clipsStore.clips.length >= 8" class="my-8">
      <AdBanner slot="2345678901" format="rectangle" />
    </div>

    <!-- Load More Button -->
    <div v-if="clipsStore.hasMoreClips" class="flex justify-center mt-8">
      <button
        @click="handleLoadMore"
        :disabled="clipsStore.loading"
        class="px-8 py-3 bg-console-panel border border-console-border rounded-full text-sm font-bold hover:border-primary hover:text-primary transition-colors disabled:opacity-50"
      >
        <span v-if="clipsStore.loading">Loading...</span>
        <span v-else>Load More</span>
      </button>
    </div>

    <!-- Total Count -->
    <div v-if="clipsStore.totalClips > 0" class="text-center mt-4 text-sm text-slate-500">
      Showing {{ clipsStore.clips.length }} of {{ clipsStore.totalClips }} clips
    </div>

    <!-- Bottom Ad Banner -->
    <div class="mt-8 mb-4">
      <AdBanner slot="3456789012" format="horizontal" />
    </div>

    <!-- SEO Footer Content -->
    <footer class="mt-12 py-8 border-t border-console-border/50 text-center">
      <div class="max-w-3xl mx-auto space-y-4 text-slate-500 text-xs">
        <h2 class="text-sm font-bold text-slate-400 mb-4">Tamil Soundboard D-PRO - Your Ultimate Tamil Meme Audio Collection</h2>
        <p>
          Welcome to Tamil Soundboard D-PRO, the largest collection of Tamil comedy audio clips and meme sounds! 
          Featuring legendary comedians like <strong>Vadivelu</strong> (வடிவேலு), <strong>Goundamani & Senthil</strong> (கவுண்டமணி, செந்தில்), 
          <strong>Santhanam</strong> (சந்தானம்), <strong>Vivek</strong> (விவேக்), and many more Tamil movie icons.
        </p>
        <p>
          Perfect for creating viral memes, WhatsApp status videos, Instagram Reels, YouTube shorts, and TikTok content. 
          Download Tamil punch dialogues, comedy scenes, and iconic movie moments instantly - all for free!
        </p>
        <div class="flex flex-wrap justify-center gap-2 pt-4">
          <span class="px-2 py-1 bg-console-panel rounded text-[10px]">Tamil Meme Audio</span>
          <span class="px-2 py-1 bg-console-panel rounded text-[10px]">Vadivelu Comedy</span>
          <span class="px-2 py-1 bg-console-panel rounded text-[10px]">Goundamani Senthil</span>
          <span class="px-2 py-1 bg-console-panel rounded text-[10px]">Tamil Movie Dialogues</span>
          <span class="px-2 py-1 bg-console-panel rounded text-[10px]">Tamil WhatsApp Status</span>
          <span class="px-2 py-1 bg-console-panel rounded text-[10px]">Kollywood Memes</span>
          <span class="px-2 py-1 bg-console-panel rounded text-[10px]">தமிழ் காமெடி</span>
        </div>
        
        <!-- Legal & Info Links -->
        <div class="flex flex-wrap justify-center gap-4 pt-6 text-slate-600">
          <router-link to="/about" class="hover:text-primary transition-colors">About</router-link>
          <router-link to="/contact" class="hover:text-primary transition-colors">Contact</router-link>
          <router-link to="/privacy" class="hover:text-primary transition-colors">Privacy Policy</router-link>
          <router-link to="/terms" class="hover:text-primary transition-colors">Terms of Service</router-link>
        </div>
        <p class="text-slate-600 pt-4">
          © {{ new Date().getFullYear() }} Tamil Soundboard. Audio clips are property of their respective copyright holders.
        </p>
      </div>
    </footer>
  </div>
</template>
